import {getI18n} from "react-i18next";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatValue} from "@reva/shared/number-format";
import type {AvmSpecParameterBound} from "@reva/shared/reva-spec";
import type {RevaSpecInputParameter} from "@reva/shared/spec-conversion";
import {format, parseISO} from "date-fns";

import type {ValueIndicationExternalValidationErrors} from "../api/payload-types/value-indication";

export interface ObjectParamError {
    /** error message (DE) */
    error_de?: string | null;
    /** error message (EN) */
    error_en?: string | null;
    /** translation key for i18n error message */
    languageKey?: string | null;
    /** optional parameter for i18n error message */
    languageParams?: object | null;
    /** optional array of relevant parameters, if this is a combined validation error */
    dependentParams?: string[] | null;
}

export type ObjectParamErrorsMap = Record<string, ObjectParamError[]>;

/**
 * Does the spec actually carry a bound? `0` is a valid bound, so this cannot be a truthiness check.
 * An empty string is treated as "no bound": it would otherwise reach `parseISO` as an invalid date.
 */
function hasBound(bound: AvmSpecParameterBound | null | undefined): bound is AvmSpecParameterBound {
    return bound !== null && bound !== undefined && bound !== "";
}

function elementBound(bound: unknown): AvmSpecParameterBound | null | undefined {
    return (Array.isArray(bound) ? bound[0] : bound) as AvmSpecParameterBound | null | undefined;
}

/** Is the entered value beyond the given bound? See {@link AvmSpecParameterBound} on comparing dates. */
function violatesBound(value: unknown, bound: AvmSpecParameterBound, side: "min" | "max"): boolean {
    if (typeof bound === "string") {
        if (typeof value !== "string") return false;
        return side === "min" ? value < bound : value > bound;
    }
    const numeric = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(numeric)) return false;
    return side === "min" ? numeric < bound : numeric > bound;
}

/** Renders a bound for the validation message: numbers localized, ISO dates as a local date. */
function formatBound(
    bound: AvmSpecParameterBound,
    parameterData: RevaSpecInputParameter,
    language: RevaLanguage,
): AvmSpecParameterBound {
    const isInteger = parameterData.type === "integer";
    if (typeof bound === "number" && (isInteger || parameterData.type === "double")) {
        const isYear = isInteger && parameterData.parameter.includes("year");
        return formatValue(bound, language, isInteger, isYear);
    }
    if (typeof bound === "string" && parameterData.type === "date") {
        return format(parseISO(bound), language === "en" ? "MM/dd/yyyy" : "dd.MM.yyyy");
    }
    return bound;
}

/**
 * Validate a single parameter value
 * @param value parameter value
 * @param parameterData parameter data
 * @return array of error objects
 */
export function validateParam(
    value: unknown,
    parameterData: RevaSpecInputParameter | null | undefined,
): ObjectParamError[] {
    const errors: ObjectParamError[] = [];
    const language = getI18n().language as RevaLanguage;

    const hasValue = value !== undefined && value !== null && value !== "";

    if (parameterData?.required && !hasValue) {
        errors.push({languageKey: "general.error.required"});
    }

    if (!hasValue || !parameterData) {
        return errors;
    }

    const {minValue, maxValue, unitDe, unitEn} = parameterData;

    // Array-Parameter tragen ihre Grenzen als einelementiges Array, sie gelten je Element (#19602)
    if (Array.isArray(value)) {
        const elementData = {
            ...parameterData,
            type: parameterData.type?.replace("[]", ""),
            required: false,
            minValue: elementBound(minValue),
            maxValue: elementBound(maxValue),
        };
        for (const error of value.flatMap((element) => validateParam(element, elementData))) {
            if (!errors.some((existing) => isSameError(existing, error))) errors.push(error);
        }
        return errors;
    }

    if (hasBound(minValue) && violatesBound(value, minValue, "min")) {
        errors.push({
            languageKey: "general.error.minValue",
            languageParams: {minValue: formatBound(minValue, parameterData, language), unitDe, unitEn},
        });
    }

    if (hasBound(maxValue) && violatesBound(value, maxValue, "max")) {
        errors.push({
            languageKey: "general.error.maxValue",
            languageParams: {maxValue: formatBound(maxValue, parameterData, language), unitDe, unitEn},
        });
    }

    return errors;
}

/** Tragen zwei Fehler dieselbe Aussage? Maßgeblich sind Meldung und beteiligte Parameter. */
function isSameError(a: ObjectParamError, b: ObjectParamError): boolean {
    return (
        a.error_de === b.error_de &&
        a.error_en === b.error_en &&
        a.languageKey === b.languageKey &&
        (a.dependentParams ?? []).join("|") === (b.dependentParams ?? []).join("|")
    );
}

/** Hängt einen Fehler an einen Parameter an, sofern dieselbe Meldung dort nicht schon steht. */
function appendUnique(errorMap: ObjectParamErrorsMap, parameter: string, error: ObjectParamError): void {
    const existing = errorMap[parameter] ?? [];
    if (existing.some((item) => isSameError(item, error))) return;
    errorMap[parameter] = [...existing, error];
}

/**
 * Append a list of external validation errors from the API to the internal object param errors map.
 *
 * Lebensdauer: Diese Fehler kommen von der AVM und werden nur beim Schreiben eines der benannten
 * Parameter wieder abgeräumt (siehe `clearErrorsForParameter` in objectParametersSlice) — eine
 * erfolgreiche Indikation löscht sie nicht, und `setParamErrors` merged nur. Das trägt, weil die
 * AVM immer genau die beteiligten Parameter benennt: was die Beanstandung auflöst, geht durch einen
 * Schreibvorgang auf einem davon. Dieselbe Beanstandung wird deshalb nicht doppelt geführt — sonst
 * wüchse die Liste bei jeder erneut scheiternden Indikation weiter (#19159).
 * @param currentErrorMap current/previous param errors map
 * @param externalValidationErrors new validation errors
 * @return updated param errors map
 */
export function appendExternalValidationErrors(
    currentErrorMap: ObjectParamErrorsMap,
    externalValidationErrors: ValueIndicationExternalValidationErrors,
): ObjectParamErrorsMap {
    const {paramErrors, combinedParamsErrors} = externalValidationErrors;

    for (const item of paramErrors) {
        appendUnique(currentErrorMap, item.param, {error_de: item.error_de, error_en: item.error_en});
    }

    for (const item of combinedParamsErrors) {
        const newValidationError = {
            error_de: item.error_de,
            error_en: item.error_en,
            dependentParams: item.params,
        };

        for (const parameter of item.params) {
            appendUnique(currentErrorMap, parameter, newValidationError);
        }
    }

    return currentErrorMap;
}
